import React, { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CommonButton } from '@/components/ui/common-button';
import { usePagination } from '@/hooks/usePagination';
import PaginationComponent from '@/components/common/PaginationComponent';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import EditRequestDetailDialog from './EditRequestDetailDialog';
import VerifyRequestDialog from './VerifyRequestDialog';
import NotVerifyRequestDialog from './NotVerifyRequestDialog';
import CancelRequestDialog from './CancelRequestDialog';
import RejectRequestDialog from './RejectRequestDialog';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import tripService from '@/services/tripService';
import { formatTableText } from '@/lib/tableUtils';
import { useAuth } from '@/contexts/AuthContext';

const EditRequestQueuePage = () => {
  const { userProfile } = useAuth();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('PENDING');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);

  const [totalItems, setTotalItems] = useState(0);
  const { currentPage, pageSize, setPage } = usePagination({ defaultPage: 1, defaultPageSize: 10 });

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [notVerifyDialogOpen, setNotVerifyDialogOpen] = useState(false);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEditRequests();
  }, [activeTab, currentPage, pageSize]);

  const fetchEditRequests = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;
      const response = await tripService.getEditRequests(
        activeTab,
        false,
        skip,
        pageSize
      );

      let dataList = [];
      let totalCount = 0;

      if (response && typeof response === 'object') {
        if (Array.isArray(response.data)) {
          dataList = response.data;
          totalCount = response.total || response.count || dataList.length;
        } else if (Array.isArray(response)) {
          dataList = response;
          totalCount = response.length;
        }
      }

      setRequests(dataList);
      setTotalItems(totalCount);
      setError(null);
    } catch (err) {
      console.error('Failed to load edit requests queue:', err);
      setError('Failed to load edit requests');
      toast({
        title: 'Error',
        description: err.message || 'Failed to load edit requests. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleOpenDetail = (request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const handleApprove = async () => {
    try {
      setSubmitting(true);
      await tripService.applyEditRequest(selectedRequest.id);
      toast({
        title: 'Edit Request Applied',
        description: `Successfully applied modifications to trip ${selectedRequest.trip_number || 'N/A'}.`,
      });
      setDetailDialogOpen(false);
      fetchEditRequests();
    } catch (err) {
      console.error(err);
      toast({
        title: 'Approval Failed',
        description: err.message || 'Failed to apply trip corrections.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmVerify = async (verificationNotes) => {
    try {
      setSubmitting(true);
      await tripService.verifyEditRequest(selectedRequest.id, verificationNotes.trim());
      toast({
        title: 'Request Verified',
        description: 'Successfully verified the trip edit request.',
      });
      setVerifyDialogOpen(false);
      setDetailDialogOpen(false);
      fetchEditRequests();
    } catch (err) {
      console.error(err);
      toast({
        title: 'Verification Failed',
        description: err.message || 'Failed to verify the edit request.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmNotVerify = async (notVerifyNotes) => {
    try {
      setSubmitting(true);
      await tripService.notVerifyEditRequest(selectedRequest.id, notVerifyNotes.trim());
      toast({
        title: 'Request Marked Not Verified',
        description: 'Successfully marked the request as not verified.',
      });
      setNotVerifyDialogOpen(false);
      setDetailDialogOpen(false);
      fetchEditRequests();
    } catch (err) {
      console.error(err);
      toast({
        title: 'Action Failed',
        description: err.message || 'Failed to mark the request as not verified.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmReject = async (rejectionReason) => {
    try {
      setSubmitting(true);
      await tripService.rejectEditRequest(selectedRequest.id, rejectionReason.trim());
      toast({
        title: 'Edit Request Rejected',
        description: 'Successfully rejected the edit request.',
      });
      setRejectDialogOpen(false);
      setDetailDialogOpen(false);
      fetchEditRequests();
    } catch (err) {
      console.error(err);
      toast({
        title: 'Rejection Failed',
        description: err.message || 'Failed to reject the edit request.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCancelRequest = async () => {
    try {
      setSubmitting(true);
      await tripService.cancelEditRequest(selectedRequest.id);
      toast({
        title: 'Request Cancelled',
        description: 'Successfully cancelled your correction request.',
      });
      setCancelDialogOpen(false);
      setDetailDialogOpen(false);
      fetchEditRequests();
    } catch (err) {
      console.error(err);
      toast({
        title: 'Cancellation Failed',
        description: err.message || 'Failed to cancel the request.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (req) => {
    const status = req.status;
    let colorClass = "text-muted-foreground";
    if (status === 'PENDING') {
      colorClass = 'text-amber-600';
    } else if (status === 'APPLIED') {
      colorClass = 'text-emerald-600';
    } else if (status === 'REJECTED') {
      colorClass = 'text-red-600';
    } else if (status === 'CANCELLED') {
      colorClass = 'text-gray-500';
    }
    return <span className={`inline-flex items-center text-xs font-semibold ${colorClass}`}>{status}</span>;
  };

  const isSelfRequester = selectedRequest?.requested_by === userProfile?.id;

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">{error}</div>;
  }

  const totalPages = Math.ceil(totalItems / pageSize);

  const getSubheaderText = () => {
    const role = userProfile?.role?.toLowerCase();
    if (role === 'port_admin') {
      return 'Verification Inbox — Review and verify whitelisted modifications to dispatched trips.';
    } else if (role === 'account_officer') {
      return 'Approval Inbox — Apply or reject verified trip modifications.';
    }
    return 'Review, audit, and approve whitelisted modifications to dispatched trips.';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Correction Tickets</h1>
          <p className="text-xs text-muted-foreground">{getSubheaderText()}</p>
        </div>

        <div className="flex gap-1 p-1 bg-secondary rounded-lg w-fit">
          {['PENDING', 'APPLIED', 'REJECTED', 'CANCELLED'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setPage(1);
              }}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${activeTab === tab
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              {tab.charAt(0) + tab.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      <TableBlurLoader isLoading={loading}>
        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform" style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {requests.length > 0 ? (
              <div className="rounded-md border overflow-hidden w-full">
                <div className="max-h-[calc(100vh-280px)] overflow-auto w-full">
                  <Table>
                    <TableHeader className="sticky top-0 z-10">
                      <TableRow>
                        <TableHead className="h-10 px-4 py-1">Trip Number</TableHead>
                        <TableHead className="h-10 px-4 py-1">Port</TableHead>
                        <TableHead className="h-10 px-4 py-1">Requester</TableHead>
                        <TableHead className="h-10 px-4 py-1 min-w-[350px]">Reason</TableHead>
                        <TableHead className="h-10 px-4 py-1">Created At</TableHead>
                        <TableHead className="h-10 px-4 py-1">Status</TableHead>
                        <TableHead className="h-10 px-4 py-1 text-right min-w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requests.map((req) => (
                        <TableRow key={req.id} className="hover:bg-transparent border-border">
                          <TableCell className="h-10 px-4 py-1 font-semibold text-primary">{formatTableText(req.trip_number)}</TableCell>
                          <TableCell className="h-10 px-4 py-1">{formatTableText(req.port_name)}</TableCell>
                          <TableCell className="h-10 px-4 py-1">{formatTableText(req.requester_username)}</TableCell>
                          <TableCell className="h-10 px-4 py-1 text-xs">{formatTableText(req.request_reason)}</TableCell>
                          <TableCell className="h-10 px-4 py-1 text-muted-foreground text-xs">{new Date(req.created_at).toLocaleString()}</TableCell>
                          <TableCell className="h-10 px-4 py-1">{getStatusBadge(req)}</TableCell>
                          <TableCell className="h-10 px-4 py-1 text-right">
                            <CommonButton
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenDetail(req)}
                              className="h-8 w-8 p-0"
                            >
                              <Eye className="h-4 w-4 text-primary" />
                            </CommonButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <PaginationComponent
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  className="border-t"
                />
              </div>
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                No edit requests available in this category
              </div>
            )}
          </div>
        </div>
      </TableBlurLoader>

      <EditRequestDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        selectedRequest={selectedRequest}
        userProfile={userProfile}
        submitting={submitting}
        activeTab={activeTab}
        onCancelClick={() => setCancelDialogOpen(true)}
        onNotVerifyClick={() => setNotVerifyDialogOpen(true)}
        onVerifyClick={() => setVerifyDialogOpen(true)}
        onRejectClick={() => setRejectDialogOpen(true)}
        onApproveClick={handleApprove}
      />

      <VerifyRequestDialog
        open={verifyDialogOpen}
        onOpenChange={setVerifyDialogOpen}
        submitting={submitting}
        onSubmit={handleConfirmVerify}
      />

      <NotVerifyRequestDialog
        open={notVerifyDialogOpen}
        onOpenChange={setNotVerifyDialogOpen}
        submitting={submitting}
        onSubmit={handleConfirmNotVerify}
      />

      <CancelRequestDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        submitting={submitting}
        onSubmit={handleConfirmCancelRequest}
      />

      <RejectRequestDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        submitting={submitting}
        onSubmit={handleConfirmReject}
      />
    </div>
  );
};

export default EditRequestQueuePage;
